import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '../shared/context/ThemeContext';
import { ToastProvider } from '../shared/context/ToastContext';
import { DocumentProvider } from '../core/state/DocumentContext';
import { AppShell } from '../components/layout/AppShell';
import { NavRoute } from '../components/layout/Sidebar';
import { Dashboard } from '../modules/dashboard/Dashboard';
import { CoverStudio } from '../modules/cover-studio/CoverStudio';
import { ReportStudio } from '../modules/lab-studio/ReportStudio';
import { DocumentStudio } from '../modules/doc-studio/DocumentStudio';
import { TemplatesView } from '../modules/templates/TemplatesView';
import { SettingsView } from '../modules/settings/SettingsView';

export const App: React.FC = () => {
  // Synchronize route with URL hash for browser history & static hosting support
  const [currentRoute, setCurrentRoute] = useState<NavRoute>(() => {
    const hash = window.location.hash.replace('#/', '').replace('#', '') as NavRoute;
    const validRoutes: NavRoute[] = ['dashboard', 'cover-studio', 'report-studio', 'document-studio', 'templates', 'settings'];
    return validRoutes.includes(hash) ? hash : 'dashboard';
  });

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#/', '').replace('#', '') as NavRoute;
      const validRoutes: NavRoute[] = ['dashboard', 'cover-studio', 'report-studio', 'document-studio', 'templates', 'settings'];
      if (validRoutes.includes(hash)) {
        setCurrentRoute(hash);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleRouteChange = (route: NavRoute) => {
    setCurrentRoute(route);
    window.location.hash = `/${route}`;
  };

  const renderActiveView = () => {
    switch (currentRoute) {
      case 'dashboard':
        return <Dashboard onNavigate={handleRouteChange} />;
      case 'cover-studio':
        return <CoverStudio />;
      case 'report-studio':
        return <ReportStudio />;
      case 'document-studio':
        return <DocumentStudio />;
      case 'templates':
        return <TemplatesView onNavigate={handleRouteChange} />;
      case 'settings':
        return <SettingsView />;
      default:
        return <Dashboard onNavigate={handleRouteChange} />;
    }
  };

  return (
    <ThemeProvider>
      <ToastProvider>
        <DocumentProvider>
          <AppShell currentRoute={currentRoute} onRouteChange={handleRouteChange}>
            {renderActiveView()}
          </AppShell>
        </DocumentProvider>
      </ToastProvider>
    </ThemeProvider>
  );
};
